import { colors, getPictures, getRoundedPhoto, getStageTitle, sendError } from "racing-renderings-library/src/utils";
import { msToTime } from "racing-renderings-library/src/canvasesUtils";
import { getWeekendData, UNAUTHORIZED_EXCEPTION_MESSAGE } from "../../restAPI";
import { hexToRgb } from "../../utils";

const fixSessionsNumber = (data) => {
  for (let i = 1; i <= data.length; i++) {
    const session = data.reduce((prev, cur) => {
      const pn = prev.sessionInfo.number;
      const cn = cur.sessionInfo.number;
      if (pn === i) return prev;
      if (cn === i) return cur;
      if (cn > i && pn > i) {
        return pn > cn ? cur : prev;
      } else return pn > cn ? prev : cur;
    });
    session.sessionInfo.number = i;
  }
};
const parseDataForWeekend = async (sessions, icons) => {
  try {
    const finalNumber = sessions.find((s) => s.sessionInfo.isFinal)?.sessionInfo?.number;
    const chartData = {
      maxPos: 0,
      drivers: [],
      raceDuration: 0,
      stages: sessions
        // .filter(s => s.sessionInfo.number <= finalNumber)
        .map((s) => ({
          isFinal: s.sessionInfo.isFinal,
          name: s.sessionInfo.name,
          short: (s.sessionInfo.shortCode ? s.sessionInfo.shortCode : getStageTitle(s.sessionInfo.name)).toUpperCase(),
          number: s.sessionInfo.number,
        })),
    };
    let countryFlag = null;
    for (const session of sessions) {
      if (session.sessionInfo.number > finalNumber) continue;
      session.data.details.forEach((details) => {
        const driverInfo = details.drivers[0];
        const driverId = driverInfo.uuid;
        const code =
          driverInfo.shortCode !== undefined && driverInfo.shortCode.trim() !== ""
            ? driverInfo.shortCode
            : driverInfo.name.split(" ")[1].substring(0, 3).toUpperCase();
        const position = details.finishPosition; // === 0 if don't CLA
        if (position > 500) return;
        if (chartData.maxPos < position && position < 200) chartData.maxPos = position;
        const color = driverInfo.colour
          ? hexToRgb(driverInfo.colour)
          : details.team.colour
          ? hexToRgb(details.team.colour)
          : colors[chartData.drivers.length % colors.length];
        const flag = {
          code: details.nationality?.name ?? "",
          src: details.nationality?.picture,
          img: null,
        };

        let driver = chartData.drivers.find((d) => d.id === driverId);
        if (driver === undefined) {
          driver = {
            id: driverId,
            name: driverInfo.name,
            code,
            miniCar: icons.cars[chartData.drivers.length % icons.cars.length],
            color,
            flag,
            stages: [],
            team: { name: details.team.name, id: details.team.uuid, carImgSrc: details.team.carIcon },
          };
          chartData.drivers.push(driver);
        }

        const stageOutput = {
          number: session.sessionInfo.number,
          name: session.sessionInfo.name,
          position,
          time: details.time,
          gap: {
            time: details.gap.timeToLead,
            laps: details.gap.lapsToLead,
          },
          laps: details.laps,
          isFinal: false,
        };
        if (session.sessionInfo.number === 1) driver.presentPos = position;
        if (session.sessionInfo.isFinal) {
          driver.finishPos = position;
          driver.gridPos = details.gridPosition;
          if (driver.gridPos === 0) driver.gridPos = driver.presentPos;
          stageOutput.move = driver.gridPos - driver.finishPos;
          const retirementInfo = session.data.retirements.find((r) => r.driver.uuid === driverId);
          if (retirementInfo !== undefined) {
            const lap = retirementInfo.lap;
            const type = retirementInfo.type;
            stageOutput.retirement = {
              img: icons[type],
              lap: lap,
              text: lap ? `Lap ${lap} ${type.toLowerCase()}` : type,
              type: type.toLowerCase(),
            };
          }
          stageOutput.isFinal = true;
          stageOutput.cla = details.classifiedStatus === "CLA";
          stageOutput.dnf = details.classifiedStatus === "DNF";
          stageOutput.points = details.points;
          if (details.best.fastest)
            stageOutput.fastest = {
              lap: details.best.lap,
              text: `Fastest lap (L${details.best.lap})`,
            };
          if (details.time > chartData.raceDuration) chartData.raceDuration = details.time;
        }
        driver.stages.push(stageOutput);
      });

      if (countryFlag === null && session.sessionInfo?.country?.picture)
        countryFlag = await getRoundedPhoto(session.sessionInfo.country.picture);
    }

    const [flags, carsImg] = await Promise.all([
      getPictures(
        chartData.drivers
          .filter((d) => d.flag?.code !== undefined && d.flag?.src !== undefined)
          .map((d) => ({ id: d.flag.code, src: d.flag.src }))
      ),
      getPictures(chartData.drivers.map((d) => ({ id: d.team.id, src: d.team.carImgSrc }))),
    ]);

    // set driver imgs
    for (const driver of chartData.drivers) {
      const car = carsImg.find((c) => c.id === driver.team.id).img;
      if (car !== undefined) driver.miniCar = car;
      driver.flag.img = flags.find((f) => f.id === driver.flag.code)?.img;
    }

    let finishLastPos = chartData.drivers.reduce((lstPos, d) => {
      if (d.finishPos === undefined) return lstPos;
      return d.finishPos > lstPos ? d.finishPos : lstPos;
    }, 0);
    let startLastPos = chartData.drivers.reduce((lstPos, d) => {
      if (d.presentPos === undefined) return lstPos;
      return d.presentPos > lstPos ? d.presentPos : lstPos;
    }, 0);
    for (const driver of chartData.drivers) {
      const finish = driver.stages.find((s) => s.isFinal);
      // set presentPos
      if (driver.presentPos === undefined) {
        startLastPos++;
        driver.presentPos = startLastPos;
      }
      // cla and retirement
      if (finish !== undefined && (finish.retirement !== undefined || finish.position === 0) && !finish.cla) {
        finishLastPos++;
        finish.position = finishLastPos;
        driver.finishPos = finishLastPos;
      }
      // absent stage
      for (let i = 1; i <= finalNumber; i++) {
        const stage = driver.stages.find((s) => s.number === i);
        const nextStage = driver.stages.find((s) => s.number > i);
        if (stage === undefined && nextStage !== undefined) {
          let lstPos = 0;
          for (const d of chartData.drivers) {
            const ds = d.stages.find((s) => s.number === i);
            if (ds !== undefined && ds.position < 500 && ds.position > lstPos) lstPos = ds.position;
          }
          driver.stages.push({
            number: i,
            position: lstPos + 1,
            absent: true,
          });
        }
      }
    }

    chartData.raceDuration = msToTime(chartData.raceDuration * 1000);
    chartData.countryFlag = countryFlag;
    return chartData;
  } catch (e) {
    sendError(e);
    return null;
  }
};

export const getData = async (id, icons) => {
  let data = await getWeekendData(id);

  if (data === UNAUTHORIZED_EXCEPTION_MESSAGE) return { error: UNAUTHORIZED_EXCEPTION_MESSAGE };
  if (data === null) {
    sendError({ name: "Fetch weekend data error", message: "no data" });
    return null;
  }
  data = data.filter((s) => s.data !== null && s.sessionInfo !== null);
  fixSessionsNumber(data);
  if (data.length === 0) {
    sendError({ name: "Fetch weekend data error", message: "no data" });
    return null;
  }
  const dateTime = data.reduce((prev, cur) => (prev.sessionInfo.number < cur.sessionInfo.number ? prev : cur))
    .sessionInfo.startTime;

  const session = data[0].sessionInfo;
  let shortTitle = `${session.series.shortName ?? session.series.name} - ${session.season.year}\n${
    session.event.shortName ?? session.event.name
  } - weekend`;
  const title = {
    shortTitle,
    name: session.series.name + " " + session.season.name + " " + session.event.name,
    date: parseInt(dateTime + "000"),
  };

  return parseDataForWeekend(data, icons).then((parse) => {
    if (parse) return { ...parse, title };
    else {
      sendError({ name: "Parse weekend data error", message: "an error occurred while processing the data" });
      return null;
    }
  });
};
